import React, {useEffect, useState, useCallback} from "react";
import {useRouter} from "next/router";
import FiltersToolbar, {LanguageOption, TagOption} from "@components/elements/FiltersToolbar";
import {ITableSorting} from "@components/elements/tables/ITableSorting";
import isEqual from "lodash.isequal";
import {ParsedUrlQueryInput} from "querystring";
import {GetServerSidePropsContext} from "next";
import cx from "classnames";
import MultiSelectField from "@components/elements/MultiSelectField";
import {SelectField} from "@components/elements/SelectField";
import {ITableSortingOption} from "@components/elements/tables/ITableSortingOption";
import {TableSortingOption} from "@components/elements/tables/TableSortingOption";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import {FiltersModal} from "@components/elements/FiltersModal";
import {ResetFilterButton} from "@components/elements/ResetFilterButton";
import {ModalHeader} from "@components/elements/ModalHeader";
import {ModalBody} from "@components/elements/ModalBody";
import {FilterModalField} from "@components/elements/FilterModalField";
import {FilterModalFieldLabel} from "@components/elements/FilterModalFieldLabel";
import {TextField} from "@components/elements/TextField";
import {PageNavigation} from "@components/elements/PageNavigation";
import {NoResults} from "@components/elements/NoResults";
import {TableRow} from "@components/elements/TableRow";
import {TableCell} from "@components/elements/TableCell";
import {Chip} from "@components/elements/Chip";
import {Loading} from "@components/elements/Loading";
import {StarRatingFilter} from "@components/elements/StarRatingFilter";
import {StarRating} from "@components/elements/StarRating";
import {TableHeader} from "@components/elements/TableHeader";
import {TableHeaderColumn} from "@components/elements/TableHeaderColumn";
import {Table} from "@components/elements/Table";
import {TableBody} from "@components/elements/TableBody";
import {NumberFilter} from "@components/elements/NumberFilter";
import {IPluginsQuery, pluginPropertyKeyToLabel,} from "@hooks/plugins/PluginsDataHooks";
import {usePluginsSearch} from "@hooks/plugins/usePluginsData";
import {DEFAULT_SORT_COLUMN, DEFAULT_SORT_DESCENDING, IPluginsSearchData} from "@components/widgets/PluginsList";
import {IoFilterCircleSharp} from "react-icons/io5";
import {SiWoocommerce, SiWordpress} from "react-icons/si";
import {formatDate, formatNumber, formatPrice} from "@utils/format";

export interface IPluginsProps {
    searchFilterValue?: string
    nameFilterValue?: string
    descriptionFilterValue?: string
    languagesFilterValues?: string[]
    tagsFilterValues?: string[]
    minRatingFilterValue?: number
    maxRatingFilterValue?: number
    minActiveInstallsFilterValue?: number
    maxActiveInstallsFilterValue?: number
    minDownloadsFilterValue?: number
    maxDownloadsFilterValue?: number
    minTotalRatingsFilterValue?: number
    maxTotalRatingsFilterValue?: number
    minLastUpdatedFilterValue?: string
    maxLastUpdatedFilterValue?: string
    minAgeFilterValue?: number
    maxAgeFilterValue?: number
    minAddedFilterValue?: string
    maxAddedFilterValue?: string
    sortColumn?: string
    sortDescending?: boolean
    page?: number
    perPage?: number
    enableQueryParams?: boolean
    renderedAt?: number
    languageOptions?: LanguageOption[]
    languageOptionsJsons?: string[]
    tagOptions?: TagOption[]
    tagOptionsJsons?: string[]
    stringifyPluginsSearchData?: string
}

export const DEFAULT_RESULTS_PER_PAGE = 50;
export const DEFAULT_MIN_RATING_FILTER_VALUE = 0;
export const DEFAULT_MAX_RATING_FILTER_VALUE = 5;
export const DEFAULT_MIN_DOWNLOADS_FILTER_VALUE = 0;
export const DEFAULT_MAX_DOWNLOADS_FILTER_VALUE = 500000000;
export const DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE = 0;
export const DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE = 5000000;
export const DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE = 0;
export const DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE = 15000;
export const DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE = '';
export const DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE = '';

PluginsTable.getServerSideProps = async (context: GetServerSidePropsContext): Promise<IPluginsProps> => {
    context.res.setHeader('Cache-Control', ['public', 's-maxage=2592000', // 30 days
        'stale-while-revalidate=604800' // 7 days
    ].join(', '));

    const getQueryParam = (camelCase: string, underscore: string) => {
        const camelValue = context.query[camelCase];
        const underscoreValue = context.query[underscore];
        
        if (typeof camelValue !== 'undefined') {
            return camelValue.toString();
        }
        if (typeof underscoreValue !== 'undefined') {
            return underscoreValue.toString();
        }
        return undefined;
    };

    const getQueryParamNumber = (camelCase: string, underscore: string, defaultValue: number) => {
        const value = getQueryParam(camelCase, underscore);
        return value !== undefined ? parseInt(value) : defaultValue;
    };

    return {
        searchFilterValue: getQueryParam('search', 'search') || '',
        nameFilterValue: getQueryParam('name', 'name') || '',
        descriptionFilterValue: getQueryParam('description', 'description') || '',
        languagesFilterValues: typeof context.query.languages !== 'undefined' ? context.query.languages.toString().split(',') : [],
        tagsFilterValues: typeof context.query.tags !== 'undefined' ? context.query.tags.toString().split(',') : [],
        sortColumn: getQueryParam('sortColumn', 'sort_column') || '',
        sortDescending: getQueryParam('sortDescending', 'sort_descending') === 'true' || false,
        page: getQueryParamNumber('page', 'page', 1),
        perPage: getQueryParamNumber('perPage', 'per_page', DEFAULT_RESULTS_PER_PAGE),
        minRatingFilterValue: getQueryParamNumber('minRating', 'min_rating', DEFAULT_MIN_RATING_FILTER_VALUE),
        maxRatingFilterValue: getQueryParamNumber('maxRating', 'max_rating', DEFAULT_MAX_RATING_FILTER_VALUE),
        minDownloadsFilterValue: getQueryParamNumber('minDownloads', 'min_downloads', DEFAULT_MIN_DOWNLOADS_FILTER_VALUE),
        maxDownloadsFilterValue: getQueryParamNumber('maxDownloads', 'max_downloads', DEFAULT_MAX_DOWNLOADS_FILTER_VALUE),
        minActiveInstallsFilterValue: getQueryParamNumber('minActiveInstalls', 'min_active_installs', DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE),
        maxActiveInstallsFilterValue: getQueryParamNumber('maxActiveInstalls', 'max_active_installs', DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE),
        minTotalRatingsFilterValue: getQueryParamNumber('minTotalRatings', 'min_total_ratings', DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE),
        maxTotalRatingsFilterValue: getQueryParamNumber('maxTotalRatings', 'max_total_ratings', DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE),
        languageOptionsJsons: [],
        tagOptionsJsons: [],
        renderedAt: Date.now(),
    };
}

function PluginsTable(props: IPluginsProps) {
    const router = useRouter();

    const [requestsCount, setRequestsCount] = useState<number>(0);

    // State handlers related to data retrieval
    const [loading, setLoading] = useState<boolean>(false);
    const [lastPluginsQuery, setLastPluginsQuery] = useState<IPluginsQuery>({});
    const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(props.languageOptions || []);
    const [tagOptions, setTagOptions] = useState<TagOption[]>(props.tagOptions || []);
    const [pluginsData, setPluginsData] = useState<IProcessedPluginData[] | null>(null);
    const [lastPluginsSearchData, setLastPluginsSearchData] = useState<IPluginsSearchData>();

    // State handlers related to data filtering
    const [searchFilterValue, setSearchFilterValue] = useState<string>(props.searchFilterValue || '');
    const [nameFilterValue, setNameFilterValue] = useState<string>(props.nameFilterValue || '');
    const [descriptionFilterValue, setDescriptionFilterValue] = useState<string>(props.descriptionFilterValue || '');
    const [tagsFilterValues, setTagsFilterValues] = useState<string[]>(props.tagsFilterValues || []);
    const [languagesFilterValues, setLanguagesFilterValues] = useState<string[]>(props.languagesFilterValues || []);
    const [minRatingFilterValue, setMinRatingFilterValue] = useState<number>(props.minRatingFilterValue || DEFAULT_MIN_RATING_FILTER_VALUE);
    const [maxRatingFilterValue, setMaxRatingFilterValue] = useState<number>(props.maxRatingFilterValue || DEFAULT_MAX_RATING_FILTER_VALUE);
    const [minActiveInstallsFilterValue, setMinActiveInstallsFilterValue] = useState<number>(props.minActiveInstallsFilterValue || DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE);
    const [maxActiveInstallsFilterValue, setMaxActiveInstallsFilterValue] = useState<number>(props.maxActiveInstallsFilterValue || DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE);
    const [minDownloadsFilterValue, setMinDownloadsFilterValue] = useState<number>(props.minDownloadsFilterValue || DEFAULT_MIN_DOWNLOADS_FILTER_VALUE);
    const [maxDownloadsFilterValue, setMaxDownloadsFilterValue] = useState<number>(props.maxDownloadsFilterValue || DEFAULT_MAX_DOWNLOADS_FILTER_VALUE);
    const [minTotalRatingsFilterValue, setMinTotalRatingsFilterValue] = useState<number>(props.minTotalRatingsFilterValue || DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE);
    const [maxTotalRatingsFilterValue, setMaxTotalRatingsFilterValue] = useState<number>(props.maxTotalRatingsFilterValue || DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE);
    const [minLastUpdatedFilterValue, setMinLastUpdatedFilterValue] = useState<string>(props.minLastUpdatedFilterValue || DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE);
    const [maxLastUpdatedFilterValue, setMaxLastUpdatedFilterValue] = useState<string>(props.maxLastUpdatedFilterValue || DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE);

    // State handlers related to data sorting
    const [sortingOptions, setSortingOptions] = useState<ITableSortingOption[]>([]);
    const [sortColumn, setSortColumn] = useState<string>(props.sortColumn || '');
    const [sortDescending, setSortDescending] = useState<boolean>(props.sortDescending ?? false);

    // State handlers related to pagination
    const [page, setPage] = useState<number>(props.page || 1);
    const [perPage] = useState<number>(props.perPage || DEFAULT_RESULTS_PER_PAGE);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalPlugins, setTotalPlugins] = useState<number>(0);

    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);

    const [enableQueryParams] = useState<boolean>(props.enableQueryParams ?? true);

    const [pluginsQuery, setPluginsQuery] = useState<IPluginsQuery | undefined>();
    const pluginsSearchData = usePluginsSearch(pluginsQuery);

    const resetFilters = useCallback(() => {
        setSearchFilterValue('');
        setNameFilterValue('');
        setDescriptionFilterValue('');
        setLanguagesFilterValues([]);
        setTagsFilterValues([]);
        setSortColumn('');
        setSortDescending(false);
        setMinRatingFilterValue(DEFAULT_MIN_RATING_FILTER_VALUE);
        setMaxRatingFilterValue(DEFAULT_MAX_RATING_FILTER_VALUE);
        setMinActiveInstallsFilterValue(DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE);
        setMaxActiveInstallsFilterValue(DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE);
        setMinDownloadsFilterValue(DEFAULT_MIN_DOWNLOADS_FILTER_VALUE);
        setMaxDownloadsFilterValue(DEFAULT_MAX_DOWNLOADS_FILTER_VALUE);
        setMinTotalRatingsFilterValue(DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE);
        setMaxTotalRatingsFilterValue(DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE);
        setMinLastUpdatedFilterValue('');
        setMaxLastUpdatedFilterValue('');
        setPage(1);
    }, []);

    const countUsedFilters = useCallback(() => {
        let count = 0;
        if (searchFilterValue) count++;
        if (nameFilterValue) count++;
        if (descriptionFilterValue) count++;
        if (languagesFilterValues.length > 0) count++;
        if (tagsFilterValues.length > 0) count++;
        if (minRatingFilterValue > DEFAULT_MIN_RATING_FILTER_VALUE) count++;
        if (maxRatingFilterValue < DEFAULT_MAX_RATING_FILTER_VALUE) count++;
        if (minActiveInstallsFilterValue > DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE) count++;
        if (maxActiveInstallsFilterValue < DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE) count++;
        if (minDownloadsFilterValue > DEFAULT_MIN_DOWNLOADS_FILTER_VALUE) count++;
        if (maxDownloadsFilterValue < DEFAULT_MAX_DOWNLOADS_FILTER_VALUE) count++;
        if (minTotalRatingsFilterValue > DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE) count++;
        if (maxTotalRatingsFilterValue < DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE) count++;
        if (minLastUpdatedFilterValue !== DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE) count++;
        if (maxLastUpdatedFilterValue !== DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE) count++;
        return count;
    }, [searchFilterValue, nameFilterValue, descriptionFilterValue, languagesFilterValues, tagsFilterValues,
        minRatingFilterValue, maxRatingFilterValue, minActiveInstallsFilterValue, maxActiveInstallsFilterValue,
        minDownloadsFilterValue, maxDownloadsFilterValue, minTotalRatingsFilterValue, maxTotalRatingsFilterValue,
        minLastUpdatedFilterValue, maxLastUpdatedFilterValue]);

    const getSortingOptions = useCallback((): ITableSortingOption[] => {
        const sortingOptions: ITableSortingOption[] = [];
        if (!pluginsData || !pluginsData.length) return sortingOptions;
        Object.keys(pluginsData[0]).forEach((key: string) => {
            if (typeof pluginsData[0][key as keyof IProcessedPluginData] === 'string') {
                sortingOptions.push(new TableSortingOption({
                    slug: key,
                    descending: false,
                    label: 'Alphabetically by ' + pluginPropertyKeyToLabel(key),
                }));
                sortingOptions.push(new TableSortingOption({
                    slug: key,
                    descending: true,
                    label: 'Alphabetically by ' + pluginPropertyKeyToLabel(key) + ' (descending)',
                }));
            } else if (typeof pluginsData[0][key as keyof IProcessedPluginData] === 'number') {
                sortingOptions.push(new TableSortingOption({
                    slug: key,
                    descending: true,
                    label: pluginPropertyKeyToLabel(key) + ' (highest first)',
                }));
                sortingOptions.push(new TableSortingOption({
                    slug: key,
                    descending: false,
                    label: pluginPropertyKeyToLabel(key) + ' (lowest first)',
                }));
            } else if (typeof pluginsData[0][key as keyof IProcessedPluginData] === 'object') {
                if (Array.isArray(pluginsData[0][key as keyof IProcessedPluginData])) {
                    sortingOptions.push(new TableSortingOption({
                        slug: key,
                        descending: true,
                        label: pluginPropertyKeyToLabel(key) + ' (most first)',
                    }));
                    sortingOptions.push(new TableSortingOption({
                        slug: key,
                        descending: false,
                        label: pluginPropertyKeyToLabel(key) + ' (least first)',
                    }));
                } else if (pluginsData[0][key as keyof IProcessedPluginData] instanceof Date) {
                    sortingOptions.push(new TableSortingOption({
                        slug: key,
                        descending: true,
                        label: pluginPropertyKeyToLabel(key) + ' (newest first)',
                    }));
                    sortingOptions.push(new TableSortingOption({
                        slug: key,
                        descending: false,
                        label: pluginPropertyKeyToLabel(key) + ' (oldest first)',
                    }));
                }
            }
        });
        return sortingOptions;
    }, [pluginsData]);

    const handleTableSortingChange = useCallback((e: ITableSorting) => {
        console.log('Sorting: ', e)
        const options = getSortingOptions();
        setSortColumn(e.column);
        setSortDescending(e.descending);
    }, [getSortingOptions]);

    const getCurrentPluginsQuery = useCallback((): IPluginsQuery => {
        return {
            page: page,
            per_page: perPage,
            search: searchFilterValue,
            name: nameFilterValue,
            description: descriptionFilterValue,
            sortColumn: sortColumn,
            sortDescending: sortDescending,
            languages: languagesFilterValues,
            tags: tagsFilterValues,
            min_rating: minRatingFilterValue,
            max_rating: maxRatingFilterValue,
            min_active_installs: minActiveInstallsFilterValue,
            max_active_installs: maxActiveInstallsFilterValue,
            min_downloads: minDownloadsFilterValue,
            max_downloads: maxDownloadsFilterValue,
            min_total_ratings: minTotalRatingsFilterValue,
            max_total_ratings: maxTotalRatingsFilterValue,
            min_last_updated: minLastUpdatedFilterValue,
            max_last_updated: maxLastUpdatedFilterValue,
        };
    }, [page, perPage, searchFilterValue, nameFilterValue, descriptionFilterValue, sortColumn, sortDescending,
        languagesFilterValues, tagsFilterValues, minRatingFilterValue, maxRatingFilterValue,
        minActiveInstallsFilterValue, maxActiveInstallsFilterValue, minDownloadsFilterValue,
        maxDownloadsFilterValue, minTotalRatingsFilterValue, maxTotalRatingsFilterValue,
        minLastUpdatedFilterValue, maxLastUpdatedFilterValue]);

    const buildParsedUrlQueryInput = useCallback((): ParsedUrlQueryInput => {
        const query: ParsedUrlQueryInput = {};

        // Only add non-default values
        if (page !== 1) query.page = page.toString();
        if (perPage !== DEFAULT_RESULTS_PER_PAGE) query.per_page = perPage.toString();
        if (searchFilterValue) query.search = searchFilterValue;
        if (nameFilterValue) query.name = nameFilterValue;
        if (descriptionFilterValue) query.description = descriptionFilterValue;
        if (sortColumn && sortColumn !== DEFAULT_SORT_COLUMN) query.sortColumn = sortColumn;
        if (sortDescending !== DEFAULT_SORT_DESCENDING) query.sortDescending = sortDescending.toString();
        if (languagesFilterValues.length > 0) query.languages = languagesFilterValues;
        if (tagsFilterValues.length > 0) query.tags = tagsFilterValues;
        
        if (minRatingFilterValue !== DEFAULT_MIN_RATING_FILTER_VALUE) 
            query.min_rating = minRatingFilterValue.toString();
        if (maxRatingFilterValue !== DEFAULT_MAX_RATING_FILTER_VALUE) 
            query.max_rating = maxRatingFilterValue.toString();
            
        if (minActiveInstallsFilterValue !== DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE) 
            query.min_active_installs = minActiveInstallsFilterValue.toString();
        if (maxActiveInstallsFilterValue !== DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE) 
            query.max_active_installs = maxActiveInstallsFilterValue.toString();
            
        if (minDownloadsFilterValue !== DEFAULT_MIN_DOWNLOADS_FILTER_VALUE) 
            query.min_downloads = minDownloadsFilterValue.toString();
        if (maxDownloadsFilterValue !== DEFAULT_MAX_DOWNLOADS_FILTER_VALUE) 
            query.max_downloads = maxDownloadsFilterValue.toString();
            
        if (minTotalRatingsFilterValue !== DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE) 
            query.min_total_ratings = minTotalRatingsFilterValue.toString();
        if (maxTotalRatingsFilterValue !== DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE) 
            query.max_total_ratings = maxTotalRatingsFilterValue.toString();

        // For date fields, include them in URL if they have a value or if they're in the URL
        if (minLastUpdatedFilterValue !== DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE) query.min_last_updated = minLastUpdatedFilterValue;
        if (maxLastUpdatedFilterValue !== DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE) query.max_last_updated = maxLastUpdatedFilterValue;

        return query;
    }, [page, perPage, searchFilterValue, nameFilterValue, descriptionFilterValue, sortColumn, sortDescending,
        languagesFilterValues, tagsFilterValues, minRatingFilterValue, maxRatingFilterValue,
        minActiveInstallsFilterValue, maxActiveInstallsFilterValue, minDownloadsFilterValue,
        maxDownloadsFilterValue, minTotalRatingsFilterValue, maxTotalRatingsFilterValue,
        minLastUpdatedFilterValue, maxLastUpdatedFilterValue]);

    const buildPluginsPageHref = useCallback((targetPage: number) => {
        const query = buildParsedUrlQueryInput();
        if (targetPage === 1) {
            delete query.page;
        } else {
            query.page = targetPage.toString();
        }
        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return;
            }
            if (Array.isArray(value)) {
                params.set(key, value.join(','));
                return;
            }
            params.set(key, String(value));
        });
        const suffix = params.toString();
        return suffix ? `${router.pathname}?${suffix}` : router.pathname;
    }, [buildParsedUrlQueryInput, router.pathname]);

    const processPluginsSearchData = useCallback((data: IPluginsSearchData) => {
        if (data.tags) {
            setTagOptions([...data.tags, ...tagOptions.filter((tagOption) => 
                tagsFilterValues.includes(tagOption.getSlug()) && 
                !data.tags?.filter((tag) => tag.getSlug() === tagOption.getSlug()).length)]);
        }

        if (data.languages) {
            setLanguageOptions([...data.languages, ...languageOptions.filter((languageOption) => 
                languagesFilterValues.includes(languageOption.getSlug()) && 
                !data.languages?.filter((language) => language.getSlug() === languageOption.getSlug()).length)]);
        }

        if (data.plugins) {
            if (data.plugins.length !== pluginsData?.length || 
                data.plugins.some((plugin, index) => plugin.pluginid !== pluginsData[index]?.pluginid)) {
                // scroll main content to top
                if (typeof document !== 'undefined') {
                    const main = document.getElementsByTagName('main')[0]
                    if (main) {
                        main.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        })
                    }
                }
            }

            setPluginsData(data.plugins);
            setTotalPages(data.totalPages || 0);
            setTotalPlugins(data.totalPlugins || 0);
            setSortingOptions(getSortingOptions());
        }

        setLoading(data.loadingPlugins ?? false);
        setLastPluginsSearchData(data);
    }, [tagOptions, tagsFilterValues, languageOptions, languagesFilterValues, pluginsData, getSortingOptions]);

    useEffect(() => {
        if (!isEqual(lastPluginsSearchData, pluginsSearchData)) {
            processPluginsSearchData(pluginsSearchData);
        }
    }, [pluginsSearchData, lastPluginsSearchData, processPluginsSearchData]);

    useEffect(() => {
        if (!isEqual(pluginsQuery, getCurrentPluginsQuery())) {
            console.log('pluginsQuery changed', pluginsQuery, getCurrentPluginsQuery());

            if (enableQueryParams) {
                router.replace({
                    query: buildParsedUrlQueryInput() as ParsedUrlQueryInput
                }, undefined, {shallow: true});
            }

            setPluginsQuery(getCurrentPluginsQuery());
        } else {
            console.log('Query has not changed');
        }
    }, [pluginsQuery, getCurrentPluginsQuery, enableQueryParams, router, buildParsedUrlQueryInput]);

    const handleSearchChange = (e: string) => {
        console.log('Search query: ', e)
        setSearchFilterValue(e);
    }

    const handleLanguagesChange = (e: string[]) => {
        console.log('Languages selected: ', e.map((option) => option))
        setLanguagesFilterValues(e);
    }

    const handleTagsChange = (e: string[]) => {
        console.log('Tags selected: ', e.map((option) => option))
        setTagsFilterValues(e);
    }

    const handlePageChange = (e: number) => {
        console.log('Page: ', e)
        setPage(e);
    }

    return (<>
        {loading && <Loading/>}
        <div className="grid grid-cols-12 gap-4 grow overflow-hidden grid-rows-[50]"
             style={{gridTemplateRows: 'auto auto 1fr'}}
        >
            <FiltersToolbar
                className={cx(" md:flex md:col-span-12 flex col-span-12 items-start content-center place-content-center hidden")}
                onResetFilters={countUsedFilters() > 0 ? resetFilters : undefined}
                onResetFiltersAskForConfirmation={countUsedFilters() > 1}
                onAdvancedFilters={() => setIsFiltersModalOpen(true)}
                activeFilters={countUsedFilters()}
            >
                <TextField
                    id="plugins-filter-search"
                    onChanged={handleSearchChange}
                    className={cx("min-w-[20rem]",)}
                    value={searchFilterValue}
                    placeholder="Search..."
                />
                <SelectField
                    id="plugins-sorting"
                    placeholder={'Sort by...'}
                    className={cx("min-w-[20rem]",)}
                    onChange={(e: ITableSortingOption) => {
                        console.log('Sort by: ', e)
                        setSortColumn(e.getSlug());
                        setSortDescending(e.getDescending());
                    }}
                    value={sortingOptions.find((option) => option.getSlug() === sortColumn && option.getDescending() === sortDescending)}
                    options={sortingOptions}
                />
            </FiltersToolbar>

            <button
                className={cx("col-span-12 flex flex-row items-center justify-center gap-2 p-2 px-4 rounded-full mx-2", "bg-gray-100 dark:bg-gray-500 dark:text-gray-100", "hover:bg-gray-200 dark:hover:bg-gray-600", "focus:outline-none focus:shadow-outline", "grow", "md:hidden")}
                onClick={() => setIsFiltersModalOpen(true)}>
                <IoFilterCircleSharp title="Filters"
                                     className="w-10 h-10 rounded-full text-gray-500 dark:text-gray-100"/>
                <span>Filters ({countUsedFilters()})</span>
            </button>

            <FiltersModal
                onClose={() => setIsFiltersModalOpen(false)}
                onResetFilters={() => resetFilters()}
                isOpen={isFiltersModalOpen}
            ><ModalHeader onClose={() => setIsFiltersModalOpen(false)}>
                <div className="flex flex-row items-center justify-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Filters
                        ({countUsedFilters()})</h1>
                    <ResetFilterButton
                        className="inline-block"
                        askConfirmation={(countUsedFilters() > 1)}
                        onReset={() => resetFilters()}
                        enabled={countUsedFilters() > 0}
                    />
                </div>
            </ModalHeader>
                <ModalBody>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <FilterModalField fullRow={true}>
                            <FilterModalFieldLabel className="text-lg font-bold">Search</FilterModalFieldLabel>
                            <TextField
                                id="plugins-filter-search"
                                onChanged={handleSearchChange}
                                className={cx("min-w-[20rem]",)}
                                value={searchFilterValue}
                                placeholder="Search..."
                            />
                        </FilterModalField>

                        <FilterModalField>
                            <FilterModalFieldLabel className="text-lg font-bold">Name</FilterModalFieldLabel>
                            <TextField
                                id="plugins-filter-search-name"
                                onChanged={setNameFilterValue}
                                className={cx("min-w-[20rem]",)}
                                value={nameFilterValue}
                                placeholder="Filter by name..."
                            />
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel className="text-lg font-bold">Description</FilterModalFieldLabel>
                            <TextField
                                id="plugins-filter-search-description"
                                onChanged={setDescriptionFilterValue}
                                className={cx("min-w-[20rem]",)}
                                value={descriptionFilterValue}
                                placeholder="Filter by description..."
                            />
                        </FilterModalField>

                        <FilterModalField>
                            <FilterModalFieldLabel className="text-lg font-bold">Tags</FilterModalFieldLabel>
                            <MultiSelectField
                                id="plugins-filter-tags"
                                onChange={handleTagsChange}
                                className={cx("min-w-[20rem]",)}
                                options={tagOptions || []}
                                values={tagsFilterValues}
                                placeholder="Filter by tags"
                            />
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel className="text-lg font-bold">Languages</FilterModalFieldLabel>
                            <MultiSelectField
                                id="plugins-filter-languages"
                                options={languageOptions || []}
                                onChange={handleLanguagesChange}
                                className={cx("min-w-[15rem]",)}
                                values={languagesFilterValues}
                                placeholder={'Filter by languages'}
                            />
                        </FilterModalField>


                        <FilterModalField fullRow={true} removeMargin={true}>
                            <FilterModalFieldLabel inLine={true} className="text-lg font-bold">
                                Rating</FilterModalFieldLabel>
                        </FilterModalField>
                        <FilterModalField inLine={true}>
                            <FilterModalFieldLabel inLine={true}>Between</FilterModalFieldLabel>
                            <StarRatingFilter
                                className="inline-block w-fit"
                                placeholder="Min rating"
                                min={0}
                                max={5}
                                step={0.5}
                                value={minRatingFilterValue}
                                onChange={(e) => setMinRatingFilterValue(e)}
                                onReset={() => setMinRatingFilterValue(0)}
                            />
                        </FilterModalField>
                        <FilterModalField inLine={true}>
                            <FilterModalFieldLabel inLine={true}>and</FilterModalFieldLabel>
                            <StarRatingFilter
                                className="inline-block w-fit"
                                placeholder="Max rating"
                                min={0}
                                max={5}
                                step={0.5}
                                value={maxRatingFilterValue}
                                onChange={(e) => setMaxRatingFilterValue(e)}
                                onReset={() => setMaxRatingFilterValue(5)}
                            />
                        </FilterModalField>

                        <FilterModalField fullRow={true} removeMargin={true}>
                            <FilterModalFieldLabel inLine={true} className="text-lg font-bold">
                                Total ratings
                            </FilterModalFieldLabel>
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel inLine={true}>between</FilterModalFieldLabel>
                            <NumberFilter
                                className="inline-block w-full"
                                placeholder="Total ratings"
                                min={0}
                                max={DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE}
                                step={10}
                                value={minTotalRatingsFilterValue}
                                acceptManualInput={true}
                                onChanged={(e) => setMinTotalRatingsFilterValue(e)}
                            />
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel inLine={true}>and</FilterModalFieldLabel>
                            <NumberFilter
                                className="inline-block w-full"
                                placeholder="Total ratings"
                                min={0}
                                max={DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE}
                                step={10}
                                value={maxTotalRatingsFilterValue}
                                acceptManualInput={true}
                                onChanged={(e) => setMaxTotalRatingsFilterValue(e)}
                            />
                        </FilterModalField>

                        <FilterModalField fullRow={true} removeMargin={true}>
                            <FilterModalFieldLabel inLine={true} className="text-lg font-bold">
                                Downloads</FilterModalFieldLabel>
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel inLine={true}>between</FilterModalFieldLabel>
                            <NumberFilter
                                className="inline-block w-full"
                                placeholder="Downloads"
                                min={0}
                                max={DEFAULT_MAX_DOWNLOADS_FILTER_VALUE}
                                step={1000}
                                value={minDownloadsFilterValue}
                                acceptManualInput={true}
                                onChanged={(e) => setMinDownloadsFilterValue(e)}
                            />
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel inLine={true}>and</FilterModalFieldLabel>
                            <NumberFilter
                                className="inline-block w-full"
                                placeholder="Downloads"
                                min={0}
                                max={DEFAULT_MAX_DOWNLOADS_FILTER_VALUE}
                                step={1000}
                                value={maxDownloadsFilterValue}
                                acceptManualInput={true}
                                onChanged={(e) => setMaxDownloadsFilterValue(e)}
                            />
                        </FilterModalField>


                        <FilterModalField fullRow={true} removeMargin={true}>
                            <FilterModalFieldLabel inLine={true} className="text-lg font-bold">
                                Active installs
                            </FilterModalFieldLabel>
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel inLine={true}>between</FilterModalFieldLabel>
                            <NumberFilter
                                className="inline-block w-full"
                                placeholder="Active installs"
                                min={0}
                                max={DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE}
                                step={1000}
                                value={minActiveInstallsFilterValue}
                                acceptManualInput={true}
                                onChanged={(e) => setMinActiveInstallsFilterValue(e)}
                            />
                        </FilterModalField>
                        <FilterModalField>
                            <FilterModalFieldLabel inLine={true}>and</FilterModalFieldLabel>
                            <NumberFilter
                                className="inline-block w-full"
                                placeholder="Active installs"
                                min={0}
                                max={DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE}
                                step={1000}
                                value={maxActiveInstallsFilterValue}
                                acceptManualInput={true}
                                onChanged={(e) => setMaxActiveInstallsFilterValue(e)}
                            />
                        </FilterModalField>


                        <FilterModalField inLine={true} fullRow={true}>
                            <ResetFilterButton
                                className="inline-block"
                                askConfirmation={(countUsedFilters() > 1)}
                                onReset={() => resetFilters()}
                                enabled={countUsedFilters() > 0}
                            />
                        </FilterModalField>
                    </div>
                </ModalBody>
            </FiltersModal>

            {pluginsData && pluginsData.length > 0 && <>
                <Table
                    className={cx()}
                    sorting={{column: sortColumn, descending: sortDescending,}}
                    onTableSortingChange={handleTableSortingChange}
                    page={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    loading={loading}
                >
                    <TableHeader
                        className={["bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 text-sm font-medium tracking-wider", "sticky top-0 z-10"].join(' ')}
                        columns={pluginsData && pluginsData.length > 0 && Object.keys(pluginsData[0]).filter((key) => !['id'].includes(key)).map((key: string) =>
                            <TableHeaderColumn
                                key={key}
                                className={[(key === Object.keys(pluginsData[0]).filter((key) => !['id'].includes(key))[0]) ? 'sticky left-0 z-[2] bg-gray-100 dark:bg-gray-800' : ''].join(' ')}
                                label={key.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                onClick={() => {
                                    setSortDescending(sortColumn === key ? !sortDescending : true);
                                    setSortColumn(key);
                                }}
                                contentClassName={[['name', 'description'].includes(key) ? 'md:w-[20rem]' : '',].join(' ')}
                                isSorted={sortColumn === key}
                                isSortedDescending={sortColumn === key ? sortDescending : false}
                                onFilterButtonClick={() => setIsFiltersModalOpen(!isFiltersModalOpen)}
                                isFiltered={(key === 'name' && nameFilterValue !== '') || (key === 'description' && descriptionFilterValue !== '') || (key === 'tags' && tagsFilterValues.length > 0) || (key === 'languages' && languagesFilterValues.length > 0) || (key === 'rating' && (minRatingFilterValue !== 0 || maxRatingFilterValue !== DEFAULT_MAX_RATING_FILTER_VALUE)) || (key === 'active_installs' && (minActiveInstallsFilterValue !== 0 || maxActiveInstallsFilterValue !== DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE)) || (key === 'total_ratings' && (minTotalRatingsFilterValue !== 0 || maxTotalRatingsFilterValue !== DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE)) || (key === 'downloaded' && (minDownloadsFilterValue !== 0 || maxDownloadsFilterValue !== DEFAULT_MAX_DOWNLOADS_FILTER_VALUE)) || (key === 'average_rating' && (minRatingFilterValue !== 0 || maxRatingFilterValue !== DEFAULT_MAX_RATING_FILTER_VALUE)) || false}
                                isFilterable={true}
                            />) || [<>No data</>]}
                    />
                    <TableBody
                        className="bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 text-sm font-medium tracking-wider"
                        rows={pluginsData && pluginsData?.length > 0 ? pluginsData.map((pluginData) => <TableRow
                            key={pluginData.pluginid}
                            className=""
                            onClick={() => {
                            }}
                            columns={Object.keys(pluginData).filter((key) => !['id'].includes(key)).map((key: string) =>
                                <TableCell
                                    key={key}
                                    className={["text-left text-sm font-medium tracking-wider dark:text-gray-300", 'align-top', (['tags', 'languages'].includes(key) ? 'min-w-[300px]' : ''), (key === Object.keys(pluginData).filter((key) => !['id'].includes(key))[0]) ? 'sticky left-0 z-[1] bg-white dark:bg-gray-900' : '',].join(' ')}
                                >
                                        {key === 'tags' && pluginData[key].length > 0 && (<>
                                            {(pluginData[key] as TagOption[]).sort((a, b) => (a.count ?? 0) > (b.count ?? 0) ? -1 : 1).slice(0, 5).map((tag: TagOption) =>
                                                <Chip
                                                    key={tag.slug}
                                                    value={tag.label + " (" + (tag.count ?? '') + ")"}
                                                    slug={tag.slug}
                                                    onClick={() => {
                                                        if (!tagsFilterValues.includes(tag.slug)) {
                                                            setTagsFilterValues([...tagsFilterValues, tag.slug]);
                                                        } else {
                                                            setTagsFilterValues(tagsFilterValues.filter((value) => value !== tag.slug));
                                                        }
                                                    }}
                                                    selected={tagsFilterValues.includes(tag.slug)}
                                                />)}
                                            {(pluginData[key] as TagOption[]).length > 5 &&
                                                <div className="flex items-center">
                                                    <button
                                                        onClick={() => {
                                                        }}
                                                        className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-sm font-medium tracking-wider"
                                                    > +{pluginData[key].length - 5} </button>
                                                </div>}
                                        </>) || key === 'languages' && pluginData[key].length > 0 && (<>
                                            {(pluginData[key] as LanguageOption[]).sort((a, b) => (a.count ?? 0) > (b.count ?? 0) ? -1 : 1).slice(0, 5).map((language: LanguageOption) =>
                                                <Chip
                                                    key={language.slug}
                                                    value={language.label + " (" + (language.count ?? '') + ")"}
                                                    slug={language.slug}
                                                    onClick={() => {
                                                        if (!languagesFilterValues.includes(language.slug)) {
                                                            setLanguagesFilterValues([...languagesFilterValues, language.slug]);
                                                        } else {
                                                            setLanguagesFilterValues(languagesFilterValues.filter((value) => value !== language.slug));
                                                        }
                                                    }}
                                                    selected={languagesFilterValues.includes(language.slug)}
                                                />)}
                                            {(pluginData[key] as LanguageOption[]).length > 5 && <div
                                                className="px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap inline-block m-1 max-w-[200px] overflow-hidden overflow-ellipsis bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 ">
                                                <button
                                                    className="flex items-center justify-center w-full h-full"> +{pluginData[key].length - 5} </button>
                                            </div>}
                                        </>) || key === 'rating' &&
                                        <StarRating rating={pluginData[key]} key={key} maxRating={5}/>
                                        || key === 'price' &&
                                        <span key={key}>{formatPrice(pluginData[key as keyof IProcessedPluginData] as any) || "—"}</span>
                                        || (key === 'wordpress_link') &&
                                        <a
                                            key={key}
                                            href={pluginData[key]?.url ?? ""}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {key === 'wordpress_link' && (
                                                pluginData.marketplace === 'woocommerce'
                                                    ? <SiWoocommerce className="w-6 h-6"/>
                                                    : <SiWordpress className="w-6 h-6"/>
                                            )}
                                        </a> || pluginData[key as keyof IProcessedPluginData] instanceof Date &&
                                        <span
                                            key={key}>{formatDate(pluginData[key as keyof IProcessedPluginData] as Date)}</span> || typeof pluginData[key as keyof IProcessedPluginData] === 'string' &&
                                        <span
                                            // key={key}>{pluginData[key as keyof IProcessedPluginData].toLocaleString()}</span>
                                            key={key}>{pluginData[key as keyof IProcessedPluginData]?.toLocaleString() ?? ""}</span> || typeof pluginData[key as keyof IProcessedPluginData] === 'number' &&
                                        <span
                                            // key={key}>{pluginData[key as keyof IProcessedPluginData].toLocaleString()}</span>
                                            key={key}>{formatNumber(pluginData[key as keyof IProcessedPluginData] as number) || ""}</span>}
                                </TableCell>)}
                        />) : []}
                    />
                </Table>
                {page && totalPages && <PageNavigation
                    className={cx("bottom-2 shadow-md rounded-md col-span-12")}
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalPlugins}
                    onPageChange={handlePageChange}
                    getPageHref={buildPluginsPageHref}
                />}
            </> || pluginsData && pluginsData.length === 0 && <NoResults
                className={['md:col-span-12', 'col-span-12', 'mx-3', "h-full",].join(' ')}
                onResetFilters={() => resetFilters()}
            />}
        </div>
    </>);
}

export default PluginsTable;
